import { DefaultContext } from '@apollo/client';
import { ServerError } from '@apollo/client/errors';
import { ErrorLink } from '@apollo/client/link/error';
import { FullStory, isInitialized } from '@fullstory/browser';
import { AxiosError } from 'axios';
import * as R from 'ramda';

import { Snackbar } from '@atom/mui';

const GQL_OPERATION_DEFINITION = 'OperationDefinition';

// Any routes that the interceptors should ignore completely.
// This list will be used to regex match against the route and
// remove it from the error handler flow.
const ignoredRoutes = ['/profile'];

enum HTTPMethods {
  GET = 'get',
  POST = 'post',
  PATCH = 'patch',
  PUT = 'put',
  DELETE = 'delete',
}

enum GQLMethods {
  QUERY = 'query',
  MUTATION = 'mutation',
}

// Prevents infinite loops of error page rendering, which could happen
// on unauthorized routes, like the /profile call used during login
const ERROR_ROUTES = ['/no-permission', '/not-found', '/unknown-error'];

const redirectToErrorPage = (path: string) => {
  if (!ERROR_ROUTES.includes(window.location.pathname)) {
    window.location.href = path;
  }
};

export const handleRetrieveMethodErrors = (status: number) => {
  switch (true) {
    case status === 403:
      redirectToErrorPage('/no-permission');
      return;
    case status === 404:
      redirectToErrorPage('/not-found');
      return;
    case status >= 500:
      redirectToErrorPage('/unknown-error');
      return;
    default:
      return;
  }
};

export const handleActionMethodErrors = (status: number) => {
  switch (true) {
    case status === 403:
      return Snackbar.error({
        message: `You don't have permission to complete this action.`,
      });
    case status === 404:
      return Snackbar.error({
        message: `The resource could not be found. It may have been deleted.`,
      });
    default:
      return '';
  }
};

export const handleErrors = (
  method: HTTPMethods | GQLMethods,
  status: number,
) => {
  switch (method) {
    case GQLMethods.QUERY:
      return handleRetrieveMethodErrors(status);
    case HTTPMethods.GET:
      return handleRetrieveMethodErrors(status);
    case GQLMethods.MUTATION:
      handleActionMethodErrors(status);
      return '';
    case HTTPMethods.POST:
      handleActionMethodErrors(status);
      return '';
    case HTTPMethods.PATCH:
      handleActionMethodErrors(status);
      return '';
    case HTTPMethods.PUT:
      handleActionMethodErrors(status);
      return '';
    case HTTPMethods.DELETE:
      handleActionMethodErrors(status);
      return '';
    default:
      return '';
  }
};

const containsIgnoredRoute = (route: string) => {
  return ignoredRoutes.reduce((acc, ignoredRoute) => {
    return acc || R.includes(ignoredRoute, route);
  }, false);
};

export const interceptAxiosError = (error: AxiosError) => {
  if (error && error?.isAxiosError) {
    const doesNotContainIgnoredRoute = !containsIgnoredRoute(
      error?.request?.responseURL,
    );

    const status = error?.response?.status;
    const method = error?.config?.method;

    if (isInitialized()) {
      FullStory('trackEvent', {
        name: 'HTTP Error',
        properties: {
          url: error?.config?.url ?? 'unknown',
          status: status ?? 0,
          method: method ?? 'unknown',
        },
      });
    }

    if (method && status && doesNotContainIgnoredRoute) {
      // @ts-ignore
      handleErrors(method, status);
    }
  }
};

export const isIgnoredError = (
  { ignoreNotFound, ignoreForbidden, ignoreServerError }: DefaultContext,
  status: number,
): boolean => {
  const isIgnoredNotFound = !!ignoreNotFound && status === 404;
  const isIgnoredForbidden = !!ignoreForbidden && status === 403;
  const isIgnoredServerError = !!ignoreServerError && status >= 500;

  return isIgnoredNotFound || isIgnoredForbidden || isIgnoredServerError;
};

export const interceptApolloError = (error: ErrorLink.ErrorHandlerOptions) => {
  // @ts-ignore
  const status = ServerError.is(error?.error) ? error.error.statusCode : null;

  const context = error?.operation?.getContext() || {};

  const method = error?.operation?.query?.definitions.find(
    definition => definition.kind === GQL_OPERATION_DEFINITION,
  )?.operation;

  if (isInitialized()) {
    FullStory('trackEvent', {
      name: 'GraphQL Error',
      properties: {
        operation: error?.operation?.operationName ?? 'unknown',
        status: status ?? 0,
        method: method ?? 'unknown',
      },
    });
  }

  if (method && status && !isIgnoredError(context, status)) {
    // @ts-ignore
    handleErrors(method, status);
  }
};
